import React, { useCallback, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { DisplayText, Icon, theme } from '@sg/garnish';
import {
  ADDED_TO_BAG_CHECK_SIZE,
  ANIMATION_DELAYS,
  getAddedLineItem,
  Upsell,
  useDynamicBagButtonLabel,
  useUpsellContent,
} from '@sg/shared-fe/features/menu';

import { useContentfulClient } from '@order/Contentful';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useShouldShowUpsellScreen, useUpsellsV2 } from '../../hooks';
import { useCart } from '../../hooks/useCart';
import { useNavigateToMainTabs } from '../../navigation';
import type { ModalStackChildProps } from '../../navigation/AppNavigation.props';
import { UpsellLineItemCard, UpsellProductOption } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const UpsellScreen = ({
  navigation,
}: ModalStackChildProps<'UpsellScreen'>) => {
  const { cart } = useCart();
  const navigateToMainTabs = useNavigateToMainTabs();

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!cart?.restaurant?.id || !cart?.restaurant?.slug) {
    navigateToMainTabs('MenuTab', { screen: 'Locations' });
    return;
  }

  const restaurantId = cart.restaurant.id;
  const restaurantSlug = cart.restaurant.slug;
  const productIdsInCart = cart.lineItems?.map((item) => item.product.id) ?? [];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <UpsellScreenReady
      navigation={navigation}
      restaurantId={restaurantId}
      restaurantSlug={restaurantSlug}
      productIdsInCart={productIdsInCart}
      cart={cart}
    />
  );
};

const UpsellScreenReady = (props: ReadyProps) => {
  const { navigation, restaurantId, restaurantSlug, productIdsInCart, cart } =
    props;

  const navigateToMainTabs = useNavigateToMainTabs();

  const { track } = useTelemetry();
  const { updateBagButtonLabel } = useDynamicBagButtonLabel();
  const shouldShowUpsellScreen = useShouldShowUpsellScreen();

  const isScreenFocused = useIsFocused();

  const contentfulClient = useContentfulClient();
  const { mappedContent } = useUpsellContent({
    contentfulClient,
    pause: !isScreenFocused,
  });

  // @DEV: We can't allow users to exit screen before upsell product stepper has resolved.
  const [openSteppers, setOpenSteppers] = useState<string[]>([]); // prettier-ignore
  const areSteppersShown = Boolean(openSteppers.length);

  const updateOpenSteppers = useCallback(
    (productId: string, isStepperShown: boolean) => {
      setOpenSteppers((openSteppers) =>
        isStepperShown
          ? [...openSteppers, productId]
          : openSteppers.filter((id) => id !== productId),
      );
    },
    [setOpenSteppers],
  );

  const { upsellProducts, productSize } = useUpsellsV2({
    productIdsInCart,
    restaurantId,
  });

  const { current: maybeAddedLineItem } = useRef(
    getAddedLineItem({
      cartLineItems: cart?.lineItems ?? [],
      upsellProducts,
    }),
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('upsell_modal.view');

  useTrackEffect('$experiment_started', {
    'Experiment name': 'upsell_modal',
    'Variant name': 'treatment',
  });

  // ─── Navigation ─────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    track('upsell_modal.close');
    if (navigation.canGoBack()) {
      navigation.goBack();

      return;
    }

    void navigateToMainTabs('MenuTab', {
      screen: 'Menu',
      params: {
        restaurantSlug,
      },
    });
  }, [track, navigateToMainTabs, restaurantSlug, navigation]);

  const handleContinue = useCallback(() => {
    navigation.replace('Bag', { referrer: 'Menu' });
  }, [navigation]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!shouldShowUpsellScreen) {
    navigateToMainTabs('MenuTab', {
      screen: 'Menu',
      params: { restaurantSlug },
    });
    return null;
  }

  return (
    <View style={styles.mainContainer}>
      <Upsell.Backdrop onPress={handleClose} />

      <Upsell.ScreenContainer style={styles.screenContainer}>
        <Upsell.Container style={styles.container}>
          <Upsell.AnimatedWrapper
            animationDelay={ANIMATION_DELAYS.ITEM_CONFIRMATION}
          >
            <Upsell.ConfirmationContainer style={styles.confirmationContainer}>
              <Upsell.ConfirmationHeader>
                <DisplayText sizeMatch={['32']}>
                  <FormattedMessage {...messages.addedToBag} />
                </DisplayText>
                <Icon
                  name="Progress4"
                  width={ADDED_TO_BAG_CHECK_SIZE}
                  height={ADDED_TO_BAG_CHECK_SIZE}
                  color={theme.colors.KALE}
                />
              </Upsell.ConfirmationHeader>

              {maybeAddedLineItem ? (
                <UpsellLineItemCard lineItem={maybeAddedLineItem} />
              ) : null}
            </Upsell.ConfirmationContainer>
          </Upsell.AnimatedWrapper>

          <Upsell.AddOnsContainer
            palette="cream"
            style={styles.addOnsContainer}
          >
            <Upsell.AnimatedWrapper
              animationDelay={ANIMATION_DELAYS.UPSELL_OPTIONS}
              style={styles.addOnsAnimatedWrapper}
            >
              <Upsell.AddOnsHeader size={productSize}>
                <DisplayText sizeMatch={['32']}>
                  {mappedContent.upsellCta}
                </DisplayText>
                <Upsell.CloseIcon
                  onPress={handleClose}
                  disabled={areSteppersShown}
                />
              </Upsell.AddOnsHeader>

              <ScrollView bounces={false}>
                <Upsell.OptionsContainer size={productSize}>
                  {upsellProducts.map((product, index) => (
                    <UpsellProductOption
                      key={product.id}
                      product={product}
                      size={productSize}
                      withBorder={index < upsellProducts.length - 1}
                      onAddToBag={updateBagButtonLabel}
                      updateOpenSteppers={updateOpenSteppers}
                      position={index}
                    />
                  ))}
                </Upsell.OptionsContainer>
              </ScrollView>

              <Upsell.Footer>
                <Upsell.ActionButton
                  variant="continue"
                  onPress={handleContinue}
                  disabled={areSteppersShown}
                />
              </Upsell.Footer>
            </Upsell.AnimatedWrapper>
          </Upsell.AddOnsContainer>
        </Upsell.Container>
      </Upsell.ScreenContainer>
    </View>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReadyProps = {
  navigation: ModalStackChildProps<'UpsellScreen'>['navigation'];
  restaurantId: string;
  restaurantSlug: string;
  productIdsInCart: string[];
  cart: NonNullable<ReturnType<typeof useCart>['cart']>;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addedToBag: {
    defaultMessage: 'Added to bag',
    description: 'Upsell Screen | Added to bag confirmation',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addOnsAnimatedWrapper: {
    flex: 1,
  },
  mainContainer: {
    flex: 1,
    paddingTop: theme.spacing['10'],
  },
  screenContainer: {
    justifyContent: 'center',
    pointerEvents: 'box-none',
  },
  container: {
    borderRadius: theme.radius.large,
    maxWidth: 1200,
    maxHeight: 840,
    alignSelf: 'center',
  },
  confirmationContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.BLACK,
    height: '100%',
  },
  addOnsContainer: {
    borderRadius: theme.radius.large,
  },
});
